<template>
  <div v-if="submitDone">
    <h2 class="heading-md mb-4">
      {{ responseMessage }}
    </h2>
  </div>
  <div v-else>
    <h2 v-if="props.data.heading" class="heading-md mb-6">
      {{ props.data.heading }}
    </h2>
    <p v-if="props.data.subheading" class="heading-sm">
      {{ props.data.subheading }}
    </p>

    <form
      id="contactForm"
      ref="formRef"
      :class="[
        'flex flex-col gap-y-6 mt-6',
        { 'opacity-50 pointer-events-none cursor-progress': isFormLoading }
      ]"
      method="POST"
      novalidate
      @submit="handleSubmit"
    >
      <InputField
        id="website"
        type="text"
        name="website"
        error-text=""
        placeholder="Website"
        :is-valid="validationResults.website"
        :required="false"
        class="input-website"
      />
      <InputField
        id="email"
        :label="`${props.data.strings.email.label}`"
        type="email"
        name="email"
        :placeholder="`${props.data.strings.email.placeholder}`"
        pattern="^[^@\s]+@[^@\s]+\.[^@\s]+$"
        required
        :error-text="`${props.data.strings.email.error}`"
        :is-valid="validationResults.email"
        @input="handleInput"
      />
      <fieldset>
        <legend class="label-lg">{{ props.data.strings.conditions.heading }}</legend>
        <InputCheckbox
          id="conditions"
          :label="`${props.data.strings.conditions.label}`"
          type="checkbox"
          name="conditions"
          style-prop="input-label--checkbox input-checkbox--secondary"
          :error-text="`${props.data.strings.conditions.error}`"
          :is-valid="validationResults.conditions"
          required
          @input="handleInput"
        />
      </fieldset>
      <button
        :class="[
          `button${!isFormValid ? ' button-disabled' : ''}`,
          props.data.strings.button_class || 'button-primary',
          'button-large'
        ]"
        type="submit"
      >
        {{ props.data.strings.button_text }}
      </button>
    </form>
  </div>
</template>
<script setup lang="ts">
import { nextTick, reactive, ref, watch } from 'vue'
import InputField from './InputField.vue'
import InputCheckbox from './InputCheckbox.vue'
import { InputFieldId } from '../modules/types'

const props = defineProps<{
  data: {
    heading?: string
    subheading?: string
    strings: {
      thankYou: string
      errorMessage: string
      serviceCategories: string
      button_text: string
      button_class: string
      email: {
        label: string
        placeholder: string
        error: string
      }
      conditions: {
        heading: string
        label: string
        error: string
      }
    }
  }
}>()

const submitDone = ref<boolean>(false)
const formRef = ref<HTMLFormElement | null>(null)
const isFormValid = ref(false)
const responseMessage = ref('')
const isFormLoading = ref(false)

type FormObject = {
  email: string
  condition: string
  field: string
  website: string
  contactSubject: string
  subheading: string
}

const formObject = reactive<FormObject>({
  email: '',
  condition: '',
  field: props.data.strings.serviceCategories,
  website: '',
  contactSubject: 'Enkelt Formulär',
  subheading: props.data.subheading ? props.data.subheading : ''
})

const validationResults = ref<Record<InputFieldId, boolean>>({
  fullName: true,
  phone: true,
  email: true,
  conditions: true,
  website: true
})

const handleInput = (id: InputFieldId, input: HTMLInputElement) => {
  if (id === 'conditions') {
    formObject.condition = input.checked.toString()
  } else {
    formObject[id as keyof FormObject] = input.value
  }
  validationResults.value[id] = input.checkValidity()

  validateForm()
}

const validateForm = () => {
  if (formRef.value) {
    isFormValid.value = formRef.value.checkValidity()
  }
}

const handleSubmit = (e: Event) => {
  e.preventDefault()

  Object.keys(validationResults.value).forEach((key) => {
    const input = formRef.value?.querySelector(`#${key}`) as HTMLInputElement
    if (input) handleInput(key as keyof typeof validationResults.value, input)
  })

  validateForm()

  if (formRef.value && !isFormValid.value) {
    formRef.value.reportValidity()
    return
  }

  isFormLoading.value = true
  sendForm()
}

const sendForm = async () => {
  try {
    const response = await fetch('/wp-json/make-equal/v1/single-contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formObject)
    })

    const data = await response.json()

    if (response.ok) {
      submitDone.value = true
      isFormLoading.value = false
      responseMessage.value = props.data.strings.thankYou
    } else if (response.status === 400) {
      if (data && data.errors) {
        console.error(data.errors)
      }
      isFormLoading.value = false
    } else {
      throw new Error('Unexpected response status')
    }
  } catch (error) {
    submitDone.value = true
    responseMessage.value = props.data.strings.errorMessage
    isFormLoading.value = false
    console.error(error)
  }
}

watch(
  () => formRef.value,
  (form) => {
    if (form) {
      const inputs = form.querySelectorAll('input')
      inputs.forEach((input) => {
        input.addEventListener('input', (e) => {
          const target = e.target as HTMLInputElement
          handleInput(target.id as InputFieldId, target)
        })
      })
    }
    nextTick(validateForm)
  }
)
</script>
<style></style>
